import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy,
  ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild, Inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  ChildTableImportService, ImportResult, ImportColumnDef,
  ImportValidationResult, CellValidationError,
} from '../../services/child-table-import.service';
import { ChildTableA11yService } from '../../services/child-table-a11y.service';
import { handleDragOver, handleDragLeave, handleDrop } from '../../core/file-utils';

export interface ImportMapping {
  csvHeader: string;
  fieldName: string;
  fieldLabel: string;
}

export interface ImportModalResult {
  success: boolean;
  recordsImported?: number;
  data?: any[];
  error?: string;
}

@Component({
  selector: 'ct-import-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './import-modal.component.html',
  styles: [`
    :host { display: block; }
    lucide-angular { display: inline-flex; align-items: center; }

    /* ── Overlay (matches global .modal-backdrop) ────────── */
    .ct-import-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(8, 8, 20, 0.28);
      backdrop-filter: blur(4px) saturate(0.82);
      -webkit-backdrop-filter: blur(4px) saturate(0.82);
      z-index: 99998;
      transition: opacity 0.22s ease;
    }
    .ct-import-wrap {
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }

    /* ── Modal shell ─────────────────────────────────────── */
    .modal-content {
      position: relative;
      width: 100%;
      max-width: 820px;
      max-height: calc(100vh - 48px);
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: #fff;
      box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.04);
    }
    .modal-header {
      padding: 20px;
      line-height: 28px;
      border-bottom: 1px solid var(--opengov-primary-white, #eee);
      background: #fafafa;
      border-radius: 8px 8px 0 0;
      display: flex;
      align-items: center;
    }
    .modal-header-left { width: calc(100% - 50px); }
    .modal-header-right { width: 50px; text-align: right; }
    .close:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: 2px; border-radius: 4px; }
    h2.modal-title {
      font-weight: 700;
      font-size: 24px;
      color: rgba(0, 0, 0, 0.8);
      letter-spacing: 0;
      margin: 0;
    }
    .modal-header p {
      line-height: 18px;
      font-size: 14px;
      color: var(--foreground-tertiary, #616365);
      font-weight: 500;
      letter-spacing: 0;
      margin: 4px 0 0;
    }
    .modal-body {
      display: flex;
      flex-direction: column;
      padding: 20px;
      overflow-y: auto;
      flex: 1 1 auto;
    }

    .ct-import-step {
      display: flex;
      flex-direction: column;
      gap: var(--og-spacing-md, 12px);
    }
    .ct-import-step--preview {
      gap: var(--og-spacing-lg, 16px);
    }
    .modal-footer-bottom {
      padding: 12px 20px;
      border-top: 1px solid #eee;
      background: #fafafa;
      border-radius: 0 0 8px 8px;
    }
    .modal-footer-bottom-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      gap: 8px;
    }
    .footer-left {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }
    .footer-right {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
    }

    /* ── Button overrides (global .btn.btn-common-primary has min-width:170px) ── */
    .modal-footer-bottom .btn.btn-common-primary {
      min-width: auto;
      width: auto;
      margin: 0;
      padding: 7px 20px;
      min-height: 36px;
      font-size: 14px;
      font-weight: 700;
      line-height: 22px;
      border: none;
      border-radius: 3px;
      background-color: var(--opengov-primary-blurple-lite, #4b3fff);
      color: var(--opengov-primary-white, #fff);
      cursor: pointer;
      transition: opacity 0.15s;
    }
    .modal-footer-bottom .btn.btn-common-primary:hover:not(:disabled):not([aria-disabled="true"]) { opacity: 0.9; }
    .modal-footer-bottom .btn.btn-common-primary:disabled,
    .modal-footer-bottom .btn.btn-common-primary[aria-disabled="true"] { opacity: 0.5; cursor: not-allowed; }
    .modal-footer-bottom .btn.btn-common-primary:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: 2px; }
    .modal-footer-bottom .btn-outline {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 7px 16px;
      min-height: 36px;
      border: 1px solid #d0d5dd;
      border-radius: 3px;
      background: #fff;
      color: #344054;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
    }
    .modal-footer-bottom .btn-outline:hover { background: #f9fafb; }
    .modal-footer-bottom .btn-outline:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: 2px; }
    .modal-footer-bottom .btn.btn-common-primary {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .modal-footer-bottom .btn.btn--secondary-action {
      background-color: #f0f4ff;
      color: var(--opengov-primary-blurple-lite, #4b3fff);
      border: 1px solid #c7d2fe;
    }
    .modal-footer-bottom .btn.btn--secondary-action:hover:not(:disabled):not([aria-disabled="true"]) {
      background-color: #e0e7ff;
      opacity: 1;
    }
    .modal-footer-bottom .btn.btn--secondary-action[aria-disabled="true"] { opacity: 0.5; cursor: not-allowed; }

    /* ── Step 1: Dropzone ────────────────────────────────── */
    .import-dropzone {
      margin: 16px 0;
      padding: 40px 20px;
      border: 2px dashed var(--og-border-default, rgba(0, 0, 0, 0.2));
      border-radius: var(--og-radius-md, 8px);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s;
      text-align: center;
    }
    .import-dropzone:hover {
      border-color: var(--og-primary, #4b3fff);
      background: rgba(75, 63, 255, 0.03);
    }
    .import-dropzone:focus-visible {
      outline: 2px solid var(--og-primary, #4b3fff);
      outline-offset: 2px;
    }
    .import-dropzone.dragover {
      border-color: var(--og-primary, #4b3fff);
      background: rgba(75, 63, 255, 0.06);
    }
    .import-dropzone.has-file {
      border-style: solid;
      border-color: var(--og-border-default, rgba(0, 0, 0, 0.12));
      cursor: default;
    }
    .dropzone-icon { color: var(--og-text-secondary, #666); margin-bottom: 4px; }
    .dropzone-label { font-size: 0.9375rem; font-weight: 500; color: var(--og-text-primary, #1a1a1a); }
    .dropzone-sublabel { font-size: 0.8125rem; color: var(--og-text-secondary, #666); }
    .dropzone-hint { font-size: 0.75rem; color: var(--og-text-disabled, #767676); margin-top: 4px; }
    .file-selected-card {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 14px;
      border-radius: 6px;
      background: #f0f4ff;
      width: 100%;
      max-width: 340px;
    }
    .file-selected-card .file-icon {
      flex-shrink: 0;
      width: 32px; height: 32px;
      border-radius: 6px;
      background: var(--og-primary, #4b3fff);
      color: #fff;
      display: flex; align-items: center; justify-content: center;
    }
    .file-selected-card .file-meta { flex: 1; min-width: 0; text-align: left; }
    .file-selected-card .file-name {
      font-size: 13px; font-weight: 600; color: #1f2937;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .file-selected-card .file-type { font-size: 11px; color: #667085; }
    app-inline-banner { display: block; }

    .template-hint {
      text-align: center;
      margin: 0;
      font-size: 13px;
      color: #667085;
    }
    .template-link {
      color: var(--og-primary, #4b3fff);
      text-decoration: underline;
      cursor: pointer;
      font-weight: 500;
      background: none;
      border: none;
      padding: 0;
      font-size: 13px;
    }
    .template-link:hover { opacity: 0.8; }
    .template-link:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: 2px; }

    /* ── Step 2: Mapping ─────────────────────────────────── */

    .section-label { margin: 0; font-size: 14px; font-weight: 600; color: #101828; }
    .section-hint { margin: 0; color: #667085; font-size: 12px; }
    .preview-header { display: flex; flex-direction: column; gap: 2px; }

    .mapping-toolbar {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .mapping-toolbar .section-label { flex: 1; min-width: 0; }
    .btn-auto-match {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border: 1px solid #d0d5dd;
      border-radius: 4px;
      background: #fff;
      color: #344054;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s;
    }
    .btn-auto-match:hover { background: #f3f0ff; border-color: var(--og-primary, #4b3fff); color: var(--og-primary, #4b3fff); }
    .btn-auto-match:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: 2px; }
    .mapping-search { position: relative; width: 180px; }
    .mapping-search > lucide-angular {
      position: absolute; top: 50%; left: 8px;
      transform: translateY(-50%); color: #98a2b3; pointer-events: none;
    }
    .mapping-search input {
      width: 100%; height: 30px;
      padding: 0 8px 0 28px;
      border: 1px solid #d0d5dd; border-radius: 4px;
      font-size: 12px; color: #334155;
    }
    .mapping-search input::placeholder { color: #98a2b3; }
    .mapping-search input:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: -1px; }

    .mapping-scroll {
      border: 1px solid #e7ebf3;
      border-radius: 6px;
      overflow: auto;
      max-height: 340px;
    }
    .mapping-grid-header,
    .mapping-grid-row {
      display: grid;
      grid-template-columns: 32px 1fr 28px minmax(0, 1.4fr);
      gap: 8px;
      padding: 8px 12px;
      align-items: center;
    }
    .mapping-grid-header {
      background: #f8fafc;
      border-bottom: 1px solid #e7ebf3;
      font-weight: 600; font-size: 12px; color: #475467;
      position: sticky; top: 0; z-index: 1;
    }
    .mapping-grid-row {
      border-bottom: 1px solid #eef1f6; font-size: 13px;
      border-left: 3px solid transparent;
      transition: border-color 0.2s;
    }
    .mapping-grid-row:last-child { border-bottom: none; }
    .mapping-row--matched { border-left-color: #86efac; background: transparent; }
    .mapping-row--required-skip { background: transparent; border-left-color: #fdba74; }
    .mapping-check { display: flex; align-items: center; justify-content: center; }
    .mapping-check input[type="checkbox"] { width: 14px; height: 14px; margin: 0; cursor: pointer; }
    .mapping-arrow { color: #98a2b3; text-align: center; font-size: 14px; }
    .mapping-col-label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #344054;
    }
    .required-badge {
      display: inline-flex;
      align-items: center;
      padding: 1px 6px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      background: transparent;
      color: #c2410c;
      border: 1px solid #fed7aa;
      white-space: nowrap;
    }
    .mapping-select-wrap {
      position: relative;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .mapping-select {
      flex: 1;
      height: 32px;
      border: 1px solid #d0d5dd; border-radius: 4px;
      padding: 0 8px; background: #fff;
      color: #1f2937; font-size: 12px; cursor: pointer;
      min-width: 0;
    }
    .mapping-select:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: -1px; }
    .mapping-select:disabled { background: #f8fafc; color: #667085; cursor: default; }
    @keyframes draw-check {
      from { stroke-dashoffset: 20; opacity: 0; }
      to   { stroke-dashoffset: 0;  opacity: 1; }
    }
    @keyframes pop-in {
      0%   { transform: scale(0.5); opacity: 0; }
      70%  { transform: scale(1.15); }
      100% { transform: scale(1);   opacity: 1; }
    }
    .mapped-check {
      flex-shrink: 0;
      width: 20px; height: 20px;
      color: #16a34a;
      animation: pop-in 0.25s ease forwards;
    }
    .mapped-check .check-circle {
      fill: #dcfce7;
      stroke: #86efac;
      stroke-width: 1.5;
    }
    .mapped-check .check-path {
      fill: none;
      stroke: #16a34a;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 20;
      animation: draw-check 0.25s ease 0.1s forwards;
      opacity: 0;
    }

    .mapping-empty {
      padding: 28px 16px;
      display: flex; flex-direction: column; align-items: center;
      text-align: center; color: #667085; gap: 8px;
    }
    .mapping-empty-icon {
      width: 48px; height: 48px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: #f4f6fb; color: #8a94a6;
    }
    .mapping-empty h5 { margin: 0; font-size: 15px; font-weight: 500; color: #101828; }
    .mapping-empty p { margin: 0; font-size: 12px; max-width: 240px; }

    /* ── Step 3: Preview + Validation ────────────────────── */
    .error-accordion {
      border: 1px solid #e7ebf3;
      border-radius: 6px;
      overflow: auto;
      max-height: 100px;
    }
    .error-group { border-bottom: 1px solid #eef1f6; }
    .error-group:last-child { border-bottom: none; }
    .error-group summary {
      padding: 8px 12px;
      font-size: 13px;
      font-weight: 600;
      color: #344054;
      cursor: pointer;
      list-style: none;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .error-group summary::-webkit-details-marker { display: none; }
    .error-group summary::before {
      content: '\\25b6';
      font-size: 10px;
      color: #98a2b3;
      transition: transform 0.15s;
    }
    .error-group[open] summary::before { transform: rotate(90deg); }
    .error-group summary:focus-visible { outline: 2px solid var(--og-primary, #4b3fff); outline-offset: -2px; }
    .error-badge {
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 20px; height: 18px; padding: 0 5px;
      border-radius: 9px;
      background: transparent;
      border: 1px solid #fca5a5;
      color: #dc2626;
      font-size: 11px; font-weight: 600;
    }
    .error-list { padding: 0 12px 8px 30px; margin: 0; list-style: none; }
    .error-item {
      padding: 4px 0;
      font-size: 12px;
      color: #667085;
      border-bottom: 1px solid #f8f9fb;
    }
    .error-item:last-child { border-bottom: none; }
    .error-item strong { color: #344054; font-weight: 600; }
    .error-item .error-expected { color: #98a2b3; font-style: italic; }
    .error-more { padding: 4px 0; font-size: 11px; color: #98a2b3; font-style: italic; }

    .preview-wrap {
      border: 1px solid #e7ebf3;
      border-radius: 6px;
      overflow: auto;
      max-height: 280px;
    }
    .preview-table {
      width: 100%; min-width: 500px;
      border-collapse: separate; border-spacing: 0;
      font-size: 12px;
    }
    .preview-table th,
    .preview-table td {
      padding: 8px 10px;
      border-bottom: 1px solid #eef1f6;
      border-right: 1px solid #eef1f6;
      white-space: nowrap;
    }
    .preview-table th:last-child,
    .preview-table td:last-child { border-right: 0; }
    .preview-table th {
      background: #f8fafc; font-weight: 600; text-align: left;
      color: #475467; position: sticky; top: 0; z-index: 1;
    }
    .preview-table td { color: #344054; background: #fff; }
    .preview-table td.cell-error {
      background: transparent;
      border-left: 3px solid #fca5a5;
      position: relative;
    }
    .cell-error-icon {
      display: inline-flex;
      flex-shrink: 0;
      width: 14px; height: 14px;
      margin-left: 4px;
      vertical-align: -2px;
    }
    .cell-error-icon .error-circle {
      fill: #fef2f2;
      stroke: #fca5a5;
      stroke-width: 1.5;
    }
    .cell-error-icon .error-path {
      fill: none;
      stroke: #dc2626;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    /* Unmapped / skipped columns in preview */
    .preview-table th.col-skipped,
    .preview-table td.col-skipped {
      opacity: 0.45;
      background: #f8f9fb;
    }
    .preview-table th.col-skipped { color: #98a2b3; }
    .ignored-badge {
      display: inline-flex;
      align-items: center;
      margin-left: 6px;
      padding: 1px 5px;
      border-radius: 3px;
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      background: #f1f5f9;
      color: #94a3b8;
      border: 1px solid #e2e8f0;
      vertical-align: middle;
    }

    .visually-hidden {
      position: absolute;
      width: 1px; height: 1px;
      padding: 0; margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap; border: 0;
    }
  `],
})
export class ImportModalComponent implements OnInit, OnDestroy {
  @ViewChild('firstFocusable') firstFocusable!: ElementRef<HTMLElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('modalContent') modalContent!: ElementRef<HTMLElement>;

  @Input() tableKey = '';
  @Input() tableTitle = '';
  @Input() fields: Array<ImportColumnDef & { fieldName: string }> = [];

  @Output() importComplete = new EventEmitter<ImportModalResult>();
  @Output() closed = new EventEmitter<void>();

  step: 'upload' | 'mapping' | 'preview' = 'upload';
  stepAnnouncement = '';
  isDragOver = false;
  csvHeaders: string[] = [];
  mappings: ImportMapping[] = [];
  importError = '';
  recordsImported = 0;
  previewData: any[] = [];
  fileName = '';
  searchTerm = '';
  fileType: 'csv' | 'xlsx' = 'csv';
  validationResult: ImportValidationResult | null = null;
  expandedErrorColumns: Set<string> = new Set();

  private allParsedRows: Record<string, any>[] = [];
  private fileContent: ArrayBuffer | string | null = null;

  constructor(
    private readonly importService: ChildTableImportService,
    private readonly a11y: ChildTableA11yService,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private readonly doc: Document,
  ) {}

  private originalParent: Node | null = null;

  ngOnInit(): void {
    this.originalParent = this.elementRef.nativeElement.parentNode;
    this.doc.body.appendChild(this.elementRef.nativeElement);
    setTimeout(() => this.firstFocusable?.nativeElement?.focus());
  }

  ngOnDestroy(): void {
    const el = this.elementRef.nativeElement;
    if (el && el.parentNode && document.body.contains(el)) {
      el.remove();
    }
  }

  // ── Computed helpers ───────────────────────────────────────

  get filteredMappings(): ImportMapping[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.mappings;
    return this.mappings.filter(m =>
      m.csvHeader.toLowerCase().includes(term) ||
      m.fieldLabel.toLowerCase().includes(term) ||
      m.fieldName.toLowerCase().includes(term));
  }

  get showEmptySearch(): boolean {
    return this.step === 'mapping' && this.searchTerm.trim() !== '' && this.filteredMappings.length === 0;
  }

  get hasMappings(): boolean {
    return this.mappings.some(m => !!m.fieldName);
  }

  get mappedFieldCount(): number {
    return this.mappings.filter(m => !!m.fieldName).length;
  }

  get matchBannerType(): 'info' | 'warn' | 'success' {
    const ratio = this.mappings.length > 0 ? this.mappedFieldCount / this.mappings.length : 0;
    if (ratio >= 0.7) return 'success';
    if (ratio > 0) return 'warn';
    return 'info';
  }

  /** CSV headers that have validation errors (errorsByColumn is keyed by csvHeader). */
  get validationErrorColumns(): string[] {
    if (!this.validationResult) return [];
    return Object.keys(this.validationResult.errorsByColumn);
  }

  trackByHeader(_index: number, mapping: ImportMapping): string {
    return mapping.csvHeader;
  }

  /** True when a CSV column is currently skipped but maps to a required field by label/name. */
  isRequiredSkip(mapping: ImportMapping): boolean {
    if (mapping.fieldName) return false;
    const norm = (s: string) => s.toLowerCase().replace(/[_\-\s]+/g, '');
    return this.fields.some(f =>
      f.required && (
        norm(f.fieldName) === norm(mapping.csvHeader) ||
        norm(f.label) === norm(mapping.csvHeader)));
  }

  /** True when another row already uses this fieldName (prevents duplicates in the dropdown). */
  isFieldUsedElsewhere(csvHeader: string, fieldName: string): boolean {
    if (!fieldName) return false;
    return this.mappings.some(m => m.csvHeader !== csvHeader && m.fieldName === fieldName);
  }

  /** Errors for a CSV column (keyed by csvHeader, not fieldName). */
  errorsForColumn(csvHeader: string): CellValidationError[] {
    return this.validationResult?.errorsByColumn[csvHeader] ?? [];
  }

  /** Column label to show in the error accordion header. */
  columnLabelForHeader(csvHeader: string): string {
    const mapping = this.mappings.find(m => m.csvHeader === csvHeader);
    return mapping?.fieldLabel || csvHeader;
  }

  cellHasError(rowIndex: number, csvHeader: string): boolean {
    return this.validationResult?.errorsByColumn[csvHeader]?.some(e => e.rowIndex === rowIndex) ?? false;
  }

  /** True if a CSV header has no mapping (will be skipped during import). */
  isColumnSkipped(csvHeader: string): boolean {
    const mapping = this.mappings.find(m => m.csvHeader === csvHeader);
    return !mapping || !mapping.fieldName;
  }

  // ── Step 1: File selection / Dropzone ──────────────────────

  openFilePicker(): void {
    this.fileInput?.nativeElement?.click();
  }

  onDragOver(event: DragEvent): void {
    handleDragOver(event);
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    handleDragLeave(event);
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    const file = handleDrop(event);
    this.isDragOver = false;
    if (file) this.processFile(file);
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    await this.processFile(file);
  }

  downloadTemplate(): void {
    const filename = `${this.tableTitle || 'data'}_template.xlsx`.toLowerCase().replace(/\s+/g, '_');
    this.importService.downloadExcelTemplate(this.fields, filename);
  }

  private focusFirstInStep(announcement: string): void {
    this.stepAnnouncement = announcement;
    this.cdr.markForCheck();
    setTimeout(() => {
      const modal = this.modalContent?.nativeElement;
      if (!modal) return;
      const focusable = modal.querySelector<HTMLElement>(
        'button:not([disabled]):not(.close), input:not([type="file"]):not([disabled]), select:not([disabled]), [tabindex="0"]'
      );
      focusable?.focus();
    }, 50);
  }

  goBackToUpload(): void {
    this.step = 'upload';
    this.fileName = '';
    this.fileContent = null;
    this.csvHeaders = [];
    this.previewData = [];
    this.allParsedRows = [];
    this.mappings = [];
    this.searchTerm = '';
    this.importError = '';
    this.isDragOver = false;
    this.validationResult = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.focusFirstInStep('Step 1 of 3: Select a file to upload');
  }

  // ── Step 2: Mapping ────────────────────────────────────────

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.cdr.markForCheck();
  }

  onMappingFieldChange(csvHeader: string, fieldName: string): void {
    const idx = this.mappings.findIndex(m => m.csvHeader === csvHeader);
    if (idx === -1) return;
    // If another row already owns this field, unmap it first (no duplicates)
    if (fieldName) {
      const ownerIdx = this.mappings.findIndex(
        m => m.csvHeader !== csvHeader && m.fieldName === fieldName);
      if (ownerIdx !== -1) {
        this.mappings[ownerIdx] = { ...this.mappings[ownerIdx], fieldName: '', fieldLabel: '' };
      }
    }
    const field = this.fields.find(f => f.fieldName === fieldName);
    this.mappings[idx] = {
      ...this.mappings[idx],
      fieldName,
      fieldLabel: field?.label ?? '',
    };
    this.cdr.markForCheck();
  }

  onMappingToggle(csvHeader: string, checked: boolean): void {
    const idx = this.mappings.findIndex(m => m.csvHeader === csvHeader);
    if (idx === -1) return;

    if (!checked) {
      this.mappings[idx] = { ...this.mappings[idx], fieldName: '', fieldLabel: '' };
    } else if (!this.mappings[idx].fieldName) {
      const match = this.fields.find(f =>
        f.fieldName.toLowerCase() === csvHeader.toLowerCase() ||
        f.label.toLowerCase() === csvHeader.toLowerCase());
      if (match) {
        this.mappings[idx] = { ...this.mappings[idx], fieldName: match.fieldName, fieldLabel: match.label };
      }
    }
    this.cdr.markForCheck();
  }

  autoMatchFields(): void {
    const normalize = (s: string) => s.toLowerCase().replace(/[_\-\s]+/g, '');
    const usedFields = new Set<string>();

    this.mappings = this.mappings.map(m => {
      const exact = this.fields.find(f =>
        !usedFields.has(f.fieldName) && (
          normalize(f.fieldName) === normalize(m.csvHeader) ||
          normalize(f.label) === normalize(m.csvHeader)));
      if (exact) {
        usedFields.add(exact.fieldName);
        return { ...m, fieldName: exact.fieldName, fieldLabel: exact.label };
      }
      const partial = this.fields.find(f =>
        !usedFields.has(f.fieldName) && (
          normalize(f.label).includes(normalize(m.csvHeader)) ||
          normalize(m.csvHeader).includes(normalize(f.label))));
      if (partial) {
        usedFields.add(partial.fieldName);
        return { ...m, fieldName: partial.fieldName, fieldLabel: partial.label };
      }
      return { ...m, fieldName: '', fieldLabel: '' };
    });
    this.cdr.markForCheck();
  }

  goToPreview(): void {
    this.importError = '';
    this.validationResult = this.importService.validateImportData(
      this.allParsedRows,
      this.fields,
      this.mappings.filter(m => !!m.fieldName),
    );
    this.expandedErrorColumns = new Set();
    this.step = 'preview';
    this.focusFirstInStep('Step 3 of 3: Review data before importing');
  }

  goBackToMapping(): void {
    this.step = 'mapping';
    this.importError = '';
    this.validationResult = null;
    this.focusFirstInStep('Step 2 of 3: Map file columns to table fields');
  }

  // ── Step 3: Preview + Import ───────────────────────────────

  executeImport(validOnly = false): void {
    if (!this.hasMappings || !this.fileContent) return;

    this.importError = '';

    const columnMapping: Record<string, string> = {};
    for (const m of this.mappings) {
      if (m.fieldName) {
        columnMapping[m.csvHeader.toLowerCase()] = m.fieldName;
      }
    }

    let result: ImportResult<any>;
    if (this.fileType === 'xlsx') {
      result = this.importService.parseXLSX(this.fileContent as ArrayBuffer, columnMapping).result;
    } else {
      result = this.importService.parseCSV(this.fileContent as string, columnMapping);
    }

    if (validOnly && this.validationResult) {
      result.data = result.data.filter((_, i) => !this.validationResult!.invalidRowIndices.has(i));
    }

    if (result.data.length > 0) {
      this.recordsImported = result.data.length;
      this.a11y.announce(`Import completed. ${this.recordsImported} rows imported successfully.`, 'assertive');
      this.importComplete.emit({ success: true, recordsImported: this.recordsImported, data: result.data });
    } else {
      this.importError = result.errors[0]?.message || 'No valid rows to import';
      this.a11y.announce(`Import failed. ${this.importError}`, 'assertive');
      this.importComplete.emit({ success: false, error: this.importError });
    }
    this.cdr.markForCheck();
  }

  // ── Close / keyboard ───────────────────────────────────────

  onClose(): void {
    this.closed.emit();
  }

  onModalKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onClose();
      return;
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    if (this.modalContent?.nativeElement) {
      this.a11y.trapFocus(event, this.modalContent.nativeElement);
    }
  }

  // ── Private ────────────────────────────────────────────────

  private async processFile(file: File): Promise<void> {
    this.importError = '';
    this.fileName = file.name;
    this.searchTerm = '';
    const ext = file.name.split('.').pop()?.toLowerCase();

    try {
      if (ext === 'xlsx' || ext === 'xls') {
        this.fileType = 'xlsx';
        this.fileContent = await file.arrayBuffer();
        this.parseAndAdvance();
      } else if (ext === 'csv' || ext === 'txt') {
        this.fileType = 'csv';
        this.fileContent = await file.text();
        this.parseAndAdvance();
      } else {
        this.importError = 'Unsupported file type. Please upload CSV, TXT, XLS or XLSX.';
      }
    } catch (error) {
      console.error('Failed to read import file', error);
      this.importError = 'Unable to read the selected file.';
    }
    this.cdr.markForCheck();
  }

  private parseAndAdvance(): void {
    if (!this.fileContent) return;

    let headers: string[] = [];
    let allRows: any[] = [];

    if (this.fileType === 'xlsx') {
      const parsed = this.importService.parseXLSX(this.fileContent as ArrayBuffer, {});
      headers = parsed.headers;
      allRows = parsed.result.data;
    } else {
      const lines = (this.fileContent as string).split(/\r?\n/).filter(l => l.trim());
      if (lines.length > 0) {
        headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const row: any = {};
          headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
          allRows.push(row);
        }
      }
    }

    if (headers.length === 0) {
      this.importError = 'Could not find headers in file.';
      return;
    }

    this.csvHeaders = headers;
    this.allParsedRows = allRows;
    this.previewData = allRows.slice(0, 5);

    this.mappings = headers.map(h => ({ csvHeader: h, fieldName: '', fieldLabel: '' }));

    this.step = 'mapping';
    this.focusFirstInStep('Step 2 of 3: Map file columns to table fields');
  }
}
